import { useEffect, useRef, useState } from "react";
import EmojiPicker from "emoji-picker-react";

const QUICK_REACTIONS = ["👍", "❤️", "🥰", "😂", "😮", "😢", "😡", "🙏", "🔥", "✨"];

/**
 * Facebook-style reaction picker. Renders an inline 10-emoji quick row;
 * a "More" button reveals the full `emoji-picker-react` panel for the
 * complete Unicode set. Calls `onSelect(emoji)` with the chosen character.
 *
 * Trigger is rendered by the parent — this component only manages the popover.
 */
export default function ReactionPicker({ open, anchorRef, onSelect, onClose }) {
  const panelRef = useRef(null);
  const [full, setFull] = useState(false);

  useEffect(() => {
    if (!open) { setFull(false); return; }
    const onDoc = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      onClose();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, onClose, anchorRef]);

  if (!open) return null;
  return (
    <div ref={panelRef}
      className="absolute z-30 bottom-full left-0 mb-2 parchment border border-secondary/40 shadow-2xl rounded-full px-2 py-1.5"
      style={{ minWidth: full ? "auto" : undefined }}
      data-testid="reaction-picker">
      {!full ? (
        <div className="flex items-center gap-0.5">
          {QUICK_REACTIONS.map((e) => (
            <button key={e} type="button"
              onClick={() => onSelect(e)}
              className="text-2xl p-1.5 rounded-full hover:bg-secondary/15 hover:scale-125 transition-all"
              data-testid={`reaction-${e}`}>
              {e}
            </button>
          ))}
          <button type="button" onClick={() => setFull(true)}
            className="text-xs px-2 py-1 ml-1 rounded-full bg-secondary/20 text-secondary hover:bg-secondary/30 uppercase tracking-wider"
            data-testid="reaction-more-btn">
            More
          </button>
        </div>
      ) : (
        <div className="bg-card rounded-md overflow-hidden" data-testid="reaction-picker-full">
          <EmojiPicker
            onEmojiClick={(d) => onSelect(d.emoji)}
            theme="dark"
            width={340}
            height={400}
            previewConfig={{ showPreview: false }}
            searchPlaceHolder="Search reactions..."
          />
        </div>
      )}
    </div>
  );
}
